import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  waTemplateService,
  WaTemplateFilters,
  CreateWaTemplateRequest,
  UpdateWaTemplateRequest,
  RenderTemplateRequest,
} from '@services/wa-template.service'

export function useWaTemplates(filters: WaTemplateFilters = {}) {
  return useQuery({
    queryKey: ['waTemplates', filters],
    queryFn: () => waTemplateService.fetchTemplates(filters),
  })
}

export function useWaTemplate(id: number) {
  return useQuery({
    queryKey: ['waTemplate', id],
    queryFn: () => waTemplateService.getTemplate(id),
    enabled: !!id,
  })
}

export function useCreateWaTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateWaTemplateRequest) => waTemplateService.createTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['waTemplates'] })
    },
  })
}

export function useUpdateWaTemplate(id: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UpdateWaTemplateRequest) => waTemplateService.updateTemplate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['waTemplates'] })
      queryClient.invalidateQueries({ queryKey: ['waTemplate', id] })
    },
  })
}

export function useDeleteWaTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => waTemplateService.deleteTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['waTemplates'] })
    },
  })
}

export function useDuplicateWaTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, newName }: { id: number; newName?: string }) =>
      waTemplateService.duplicateTemplate(id, newName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['waTemplates'] })
    },
  })
}

export function useRenderWaTemplatePreview() {
  return useMutation({
    mutationFn: (data: RenderTemplateRequest) => waTemplateService.renderPreview(data),
  })
}

export function useSubmitWaTemplateForApproval() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => waTemplateService.submitForApproval(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['waTemplates'] })
      queryClient.invalidateQueries({ queryKey: ['waTemplate', id] })
    },
  })
}

export function useSyncWaTemplates() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (accountId: number) => waTemplateService.syncWithProvider(accountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['waTemplates'] })
    },
  })
}
